import { Text } from "@ttc3k/trekker";
import { OperatorWithStatus } from "gql/generated";
import { InfoCircle, List } from "iconoir-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AlphabetJumper,
  BundleHeader,
  EmptyState,
  GroupRenderer,
  PageHeader,
} from "shared/components";
import { useLoggedInAs } from "shared/hooks";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { useListItems } from "./useListItems";
import { useCustomListDetailsContext } from "../../CustomListDetailsContext";
import { useVirtualizer } from "@tanstack/react-virtual";
import { OperatorCardRenderer } from "shared/components/organisms";

type ListItemsProps = {
  virtualScrollRef: React.RefObject<HTMLDivElement | null>;
  onEditItemsClick: () => void;
  onOperatorClick: (operator: OperatorWithStatus) => void;
  onOperatorEntityIdsChange: (operatorVersionIds: string[]) => void;
};

export const ListItems = ({
  virtualScrollRef,
  onEditItemsClick,
  onOperatorClick,
  onOperatorEntityIdsChange,
}: ListItemsProps) => {
  const { t } = useTranslation();

  const loggedInAs = useLoggedInAs();
  const organizationId = loggedInAs?.entityId;

  const { list } = useCustomListDetailsContext();
  const translatedDescription = useMemo(
    () => getMultilingualString(list.description),
    [list],
  );

  const {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    numOperators,
    searchValue,
    showAlphabet,
    onSearchValueChange,
  } = useListItems({
    operators: list.entities as OperatorWithStatus[],
    onOperatorEntityIdsChange,
  });

  // Setup the virtualizer
  const virtualizer = useVirtualizer({
    count: flattenedAlphabetGroups.length,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => 84,
    overscan: 15,
    scrollMargin: virtualScrollRef.current?.offsetTop ?? 0,
  });

  // Handle the letter click from the alphabet jumper
  const handleLetterClick = (letter: string) => {
    // Find the index of the letter in the flattened alphabet groups and get the item
    const index = flattenedAlphabetGroups.findIndex(
      (group) => group.letter === letter.toLowerCase(),
    );
    const item = flattenedAlphabetGroups[index];
    if (item) {
      // Calculate the offset to scroll to
      // NOTE 412 is the height to the bottomof the bundle header
      const offset =
        item.virtualizerOffset +
        (index === 0
          ? 0
          : item.virtualizerOffset > 278
            ? 278
            : (virtualizer.scrollOffset ?? 0));
      virtualizer.scrollToOffset(offset, {
        align: "start",
        behavior: "smooth",
      });
    }
  };

  return (
    <Flex flexDir={"column"} gap={"300"}>
      <PageHeader
        title={t("customLists:LIST_DETAILS.TABS.LIST_ITEMS")}
        Icon={List}
        ctaButtonCopy={t("customLists:LIST_DETAILS.CTA_BUTTONS.EDIT_COPY")}
        ctaButtonProps={{
          onClick: onEditItemsClick,
        }}
      />

      {translatedDescription && <Text>{translatedDescription}</Text>}

      <BundleHeader
        searchProps={{
          value: searchValue,
          onValueChange: onSearchValueChange,
        }}
        count={numOperators}
      />

      <Flex
        flexDir="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="400"
        marginTop="400"
      >
        {/* We're using a regular div here so we can easily and dynamically set the style */}
        <div
          style={{
            position: "relative",
            width: "100%",
            height: `${virtualizedHeight}px`,
          }}
        >
          {numOperators < 1 && (
            <EmptyState text={t("customLists:LIST_DETAILS.PAGE.EMPTY")} />
          )}
          {virtualizer.getVirtualItems().map((item) => {
            const alphabetItem = flattenedAlphabetGroups[item.index];
            const height = alphabetItem.type === "letter" ? "37px" : "84px";

            return (
              <div
                key={item.key}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height,
                  maxHeight: height,
                  transform: `translateY(${alphabetItem.virtualizerOffset}px)`,
                }}
              >
                {alphabetItem.type === "letter" ? (
                  <GroupRenderer
                    title={alphabetItem.letter.toUpperCase()}
                    showTitle={true}
                  />
                ) : (
                  OperatorCardRenderer(
                    alphabetItem.operator as OperatorWithStatus,
                    organizationId,
                    t,
                    {
                      Icon: InfoCircle,
                      onClick: () =>
                        onOperatorClick(
                          alphabetItem.operator as OperatorWithStatus,
                        ),
                    },
                  )
                )}
              </div>
            );
          })}
        </div>
        {showAlphabet && (
          <AlphabetJumper
            alphabetGroups={alphabetGroups}
            jumpBase="GroupRendererAnchor_"
            mt={"400"}
            top={"100px"}
            onLetterClick={handleLetterClick}
          />
        )}
      </Flex>
    </Flex>
  );
};
